import { useCallback, useEffect, useMemo } from 'react';
import { logout, refreshTokens } from '../services/authService.js';
import { isTokenExpired, useAuthStore } from '../stores/authStore.js';
import { useCommonCurrentUser } from '../utils/api.js';
import { setupAuthListener } from '../utils/firebase.js';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider component that provides authentication context to the application.
 * It handles token validation, user authentication state, and auto-refresh of user data.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { tokens } = useAuthStore();
  const isStudentPath = useMemo(() => typeof window !== 'undefined' && window.location.pathname.startsWith('/app/') || window.location.pathname.startsWith('/auth/'), []);
  const { refetch } = useCommonCurrentUser({ enabled: false });

  // Handle logout
  const handleLogout = useCallback(() => {
    logout();
    // Optionally redirect to login page
    window.location.href = '/auth/login';
  }, []);

  // Handle token refresh
  const handleTokenRefresh = useCallback(async () => {
    try {
      await refreshTokens();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      handleLogout();
    }
  }, [handleLogout]);

  // Set up Firebase auth state listener
  useEffect(() => {
    // Setup Firebase auth state listener
    const unsubscribe = setupAuthListener();

    return () => {
      // Clean up the listener when component unmounts
      unsubscribe();
    };
  }, []);

  // Set up token refresh mechanism
  useEffect(() => {
    if (!tokens) return;

    // Function to check and refresh token if needed
    const checkAndRefreshToken = async () => {
      // Check if token is about to expire (within the next 10 minutes)
      if (isTokenExpired(tokens.expiresAt, 600)) {
        await handleTokenRefresh();
      }
    };

    // Run immediately to check current token status
    checkAndRefreshToken();

    // Set up interval to regularly check token expiration
    // Check every 5 minutes
    const refreshInterval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [tokens, handleTokenRefresh]);

  useEffect(() => {
    if (tokens) {
      refetch();
    }
  }, [tokens, refetch]);

  return <>{children}</>;
};

export default AuthProvider; 